/**
 * What: The ready-made study setup an Opportunity saves ahead of time (#4045): one product from one
 *   Customer Insights answer, the field the insight's rule allowed and the AI chose (any enabled kind, F5),
 *   its current value, up to 3 real alternatives, the full record of the choice, the stated audience,
 *   questions and run settings. Test this turns it into a draft with no AI call.
 * Transport / data-flow: Written by the Opportunity task into its `ai_generation_batches` output; read
 *   by Admin, the Customer Insights card and the seed route. Plan:
 *   https://github.com/Yesterday-Work/ssr-validation-tool/blob/fdcf45fb4/docs/plans/customer-insights-opportunity-handoff.md.
 * LLM INSTRUCTION: DO NOT add a fallback product, field, market or placeholder alternative. A
 *   semantic decline is retained as `no_match`; R9 (no distinct alternative) stays `unavailable`.
 *   DO NOT accept a pick that is not on the shortlist or not of an allowed kind.
 */
import * as z from 'zod';
import { merchantStoreGoalIdSchema, researchProvenanceSchema, validateResearchEvidenceUses, } from '@testfeed/integration-contracts';
import { normalizePlainDescriptionText } from './description-html.js';
import { metafieldSemanticRoleSchema } from './product-semantic-enrichment.js';
import { getConceptDefinitionByVariantType } from './product-research-registry.js';
import { SHOPIFY_MARKETING_VARIANT_TYPES } from './product-research-inputs.js';
import { OPPORTUNITY_ENABLED_KINDS, OPPORTUNITY_INSIGHT_RULES, OPPORTUNITY_QUESTIONS, opportunityQuestionIdForKind, } from './opportunity-insight-rules.js';
import { resolveShopifyTreatmentField, SHOPIFY_DESCRIPTION_VARIANT_MAX_LENGTH } from './shopify-treatment-field.js';
/** Requirements R1-R10 and the reason each one logs when it fails, in check order. */
export const OPPORTUNITY_REQUIREMENTS = [
    { id: 'R1', reasons: ['store_not_connected'] },
    { id: 'R2', reasons: ['no_build', 'exports_running', 'exports_failed'] },
    { id: 'R3', reasons: ['insight_unavailable', 'insight_not_saved'] },
    { id: 'R4', reasons: ['product_id_invalid'] },
    { id: 'R5', reasons: ['product_not_active'] },
    { id: 'R6', reasons: ['experiment_type_unsupported', 'no_field_for_question'] },
    { id: 'R7', reasons: ['no_supported_market'] },
    { id: 'R8', reasons: ['model_unavailable', 'models_differ', 'sample_size_unavailable'] },
    { id: 'R9', reasons: ['no_distinct_alternative'] },
    { id: 'R10', reasons: ['OPPORTUNITY_SUGGESTION_STALE', 'OPPORTUNITY_INPUT_LINEAGE_STALE'] },
];
const sha256 = z.string().regex(/^sha256:[0-9a-f]{64}$/);
const shopifyProductId = z.string().regex(/^gid:\/\/shopify\/Product\/\d+$/);
const text = z.string().trim().min(1);
export const opportunityKeySchema = z.strictObject({
    connectionId: text,
    definitionId: z.literal('product-history'),
    productId: shopifyProductId,
});
const origin = z.strictObject({
    kind: z.literal('customer_insight'),
    /** Stable saved insight ID (`saved_insights.id`, #4103 D6): the lineage link. Provenance, not identity. */
    insightId: text,
    /** The saved read and metric result this idea was built from (exact lineage). Absent only on ideas prepared before
     * 3 Nov 2026; Web refuses a new snapshot without them. Provenance: never part of the AI material. */
    readId: text.optional(),
    metricResultId: text.optional(),
    definitionId: z.literal('product-history'),
    inputFingerprint: sha256,
    figure: z.strictObject({ customers: z.number().int().nonnegative(), buyers: z.number().int().positive() }),
    ordersSyncedAt: z.iso.datetime({ offset: true }),
});
const product = z.strictObject({
    productId: shopifyProductId,
    title: text,
    updatedAt: z.iso.datetime({ offset: true }).nullable(),
});
// Historical setups keep their original internal kinds; the current rule emits only enabled public kinds.
const fieldKind = z.enum(SHOPIFY_MARKETING_VARIANT_TYPES);
/** How a kind is tested (for example price sets the variant price, a claim is appended to the description). */
export function opportunityComparisonForKind(kind) {
    const elementType = getConceptDefinitionByVariantType(kind).contracts?.elementType;
    const treatment = elementType ? resolveShopifyTreatmentField(elementType) : null;
    if (!treatment)
        throw new Error(`OPPORTUNITY_KIND_UNTESTABLE: ${kind} has no treatment field`);
    return { treatmentField: treatment.field, action: treatment.resolution.action };
}
/** One field offered to the AI: where it lives, what it means, which layer the meaning came from, its value. */
export const opportunityShortlistFieldSchema = z.strictObject({
    researchInputId: text,
    namespace: text,
    key: text,
    variantId: text.nullable(),
    label: text,
    /** The rule's allowed kinds this field has. */
    kinds: z.array(fieldKind).min(1),
    /** Meanings, main first. */
    roles: z.array(metafieldSemanticRoleSchema).min(1),
    layer: z.enum(['rule', 'ai', 'merchant']),
    valueFingerprint: text,
});
/** The full lineage of the choice, so results can be compared by rule, goal, kind and meaning layer over time. */
const choice = z.strictObject({
    rule: z.strictObject({ id: text, version: z.number().int().positive() }),
    goal: merchantStoreGoalIdSchema.nullable(),
    researchQuestionId: text,
    allowedKinds: z.array(fieldKind).min(1),
    experimentType: text,
    shortlist: z.array(opportunityShortlistFieldSchema).min(1),
    pick: z.strictObject({ researchInputId: text, kind: fieldKind, reason: text }),
});
const alternative = z.strictObject({
    id: text,
    title: text,
    value: text.max(SHOPIFY_DESCRIPTION_VARIANT_MAX_LENGTH),
});
const PRICE = /^\d+(\.\d{1,2})?$/;
/** Text appended to the description (claims, how-to-use) stays short; a description may be complete. */
export const OPPORTUNITY_APPENDED_TEXT_MAX_LENGTH = 600;
/** Compares values the way a shopper would read them: whitespace for text, the amount for a price. */
export function sameOpportunityValue(kind, a, b) {
    return kind === 'price'
        ? Number(a) === Number(b)
        : normalizePlainDescriptionText(a) === normalizePlainDescriptionText(b);
}
/** Why an alternative cannot be tested against the current value, or null. One owner for the schema and the AI step. */
export function opportunityAlternativeProblem(kind, current, value) {
    if (kind === 'price') {
        if (!PRICE.test(value) || Number(value) <= 0)
            return 'not_a_price';
        const base = Number(current);
        // ponytail: fixed 0.5x-2x band around today's price, as Study Builder price suggestions; widen if bolder tests are wanted.
        if (base > 0 && (Number(value) < base * 0.5 || Number(value) > base * 2))
            return 'price_out_of_band';
    }
    else if (value.length >
        (kind === 'description' ? SHOPIFY_DESCRIPTION_VARIANT_MAX_LENGTH : OPPORTUNITY_APPENDED_TEXT_MAX_LENGTH))
        return 'too_long';
    return sameOpportunityValue(kind, current, value) ? 'same_as_current' : null;
}
function checkChoice(value, ctx) {
    const picked = value.shortlist.find((field) => field.researchInputId === value.pick.researchInputId);
    if (!picked)
        ctx.addIssue({ code: 'custom', message: 'OPPORTUNITY_SETUP_INVALID: the pick is not on the shortlist' });
    else if (!picked.kinds.includes(value.pick.kind))
        ctx.addIssue({ code: 'custom', message: 'OPPORTUNITY_SETUP_INVALID: the picked field does not have that kind' });
    for (const field of value.shortlist)
        if (field.kinds.some((kind) => !value.allowedKinds.includes(kind)))
            ctx.addIssue({ code: 'custom', message: 'OPPORTUNITY_SETUP_INVALID: a shortlist kind the rule does not allow' });
    const currentRule = OPPORTUNITY_INSIGHT_RULES['product-history'];
    if (value.rule.id === currentRule.id && value.rule.version === currentRule.version) {
        const currentKinds = new Set(OPPORTUNITY_ENABLED_KINDS);
        const hasDisabledKind = [
            ...value.allowedKinds,
            ...value.shortlist.flatMap((field) => field.kinds),
            value.pick.kind,
        ].some((kind) => !currentKinds.has(kind));
        if (hasDisabledKind)
            ctx.addIssue({ code: 'custom', message: 'OPPORTUNITY_SETUP_INVALID: the current rule uses a disabled kind' });
    }
    return picked;
}
const readyFields = z.strictObject({
    status: z.literal('ready'),
    research: researchProvenanceSchema.optional(),
    opportunityKey: opportunityKeySchema,
    origin,
    product,
    choice,
    question: z.strictObject({ research: text, respondentFollowUp: text }),
    /** The chosen field's current value, read live from Shopify. */
    control: z.strictObject({ value: text, valueFingerprint: text }),
    alternatives: z.array(alternative).min(1).max(3),
    comparison: z.strictObject({ treatmentField: text, action: text }),
    audience: z.strictObject({ kind: z.literal('broad'), market: z.enum(['AU', 'US']), label: text }),
    coreQuestions: z.tuple([z.literal('understanding'), z.literal('purchase_intent'), z.literal('impression')]),
    run: z.strictObject({ sampleSize: z.number().int().positive(), model: text }),
    trace: z.strictObject({
        taskId: text,
        opportunityModel: text,
        /** The alternatives step's model (config key `opportunity_rewrite_model`). */
        rewriteModel: text,
        promptVersions: z.strictObject({
            opportunity: z.number().int().positive(),
            rewrite: z.number().int().positive(),
        }),
        generatedAt: z.iso.datetime({ offset: true }),
    }),
    limits: z.literal('Directional concept evidence from simulated shoppers. Not a sales or retention result.'),
});
const readySetup = readyFields.superRefine((setup, ctx) => {
    if (setup.research?.status === 'retained') {
        try {
            validateResearchEvidenceUses(setup.research.evidenceUses, setup.research.selection, setup.alternatives.map((item) => item.id));
        }
        catch {
            console.error('RESEARCH_EVIDENCE_USE_INVALID');
            ctx.addIssue({ code: 'custom', message: 'RESEARCH_EVIDENCE_USE_INVALID' });
        }
    }
    if (setup.opportunityKey.productId !== setup.product.productId)
        ctx.addIssue({ code: 'custom', message: 'OPPORTUNITY_SETUP_INVALID: key and product differ' });
    const picked = checkChoice(setup.choice, ctx);
    if (picked && picked.valueFingerprint !== setup.control.valueFingerprint)
        ctx.addIssue({ code: 'custom', message: 'OPPORTUNITY_SETUP_INVALID: control is not the picked field' });
    const kind = setup.choice.pick.kind;
    const expected = opportunityComparisonForKind(kind);
    if (setup.comparison.treatmentField !== expected.treatmentField || setup.comparison.action !== expected.action)
        ctx.addIssue({ code: 'custom', message: 'OPPORTUNITY_SETUP_INVALID: comparison does not match the field kind' });
    const ids = new Set();
    const seen = [setup.control.value];
    for (const item of setup.alternatives) {
        const problem = opportunityAlternativeProblem(kind, setup.control.value, item.value);
        if (problem && problem !== 'same_as_current')
            ctx.addIssue({ code: 'custom', message: `OPPORTUNITY_SETUP_INVALID: alternative ${problem}` });
        // An alternative that reads the same as today's value, or another alternative, would test nothing (#4049 D2e).
        if (ids.has(item.id) || seen.some((other) => sameOpportunityValue(kind, other, item.value)))
            ctx.addIssue({ code: 'custom', message: 'OPPORTUNITY_SETUP_INVALID: alternative repeats another value' });
        ids.add(item.id);
        seen.push(item.value);
    }
});
const unavailableSetup = z
    .strictObject({
    status: z.literal('unavailable'),
    reason: z.literal('no_distinct_alternative'),
    research: researchProvenanceSchema.optional(),
    opportunityKey: opportunityKeySchema,
    origin,
    product,
    choice,
})
    .superRefine((setup, ctx) => void checkChoice(setup.choice, ctx));
/** A semantic decline is a retained chooser result, not an alternatives failure or a missing setup. */
const noMatchSetup = z
    .strictObject({
    status: z.literal('no_match'),
    reason: z.literal('semantic_no_match'),
    research: researchProvenanceSchema.optional(),
    diagnostic: text,
    opportunityKey: opportunityKeySchema,
    origin,
    product,
})
    .superRefine((setup, ctx) => {
    if (setup.opportunityKey.productId !== setup.product.productId)
        ctx.addIssue({ code: 'custom', message: 'OPPORTUNITY_SETUP_INVALID: key and product differ' });
});
/** Retained content keeps its original question prose; today's recipe only governs new generation. */
export const retainedOpportunityStudySetupSchema = z.discriminatedUnion('status', [
    readySetup,
    unavailableSetup,
    noMatchSetup,
]);
export const opportunityStudySetupSchema = retainedOpportunityStudySetupSchema.superRefine((setup, ctx) => {
    if (setup.status !== 'ready')
        return;
    const currentRule = OPPORTUNITY_INSIGHT_RULES['product-history'];
    if (setup.choice.rule.id === currentRule.id && setup.choice.rule.version === currentRule.version) {
        const questionId = opportunityQuestionIdForKind(setup.choice.pick.kind);
        if (setup.choice.researchQuestionId !== questionId ||
            setup.question.research !== OPPORTUNITY_QUESTIONS[questionId] ||
            setup.question.respondentFollowUp !== currentRule.respondentFollowUp)
            ctx.addIssue({ code: 'custom', message: 'OPPORTUNITY_SETUP_INVALID: unsupported question contract' });
    }
});
const requirementId = z.enum(OPPORTUNITY_REQUIREMENTS.map((r) => r.id));
const requirementFailure = z.enum(OPPORTUNITY_REQUIREMENTS.flatMap((r) => [...r.reasons]));
export const opportunityRequirementResultSchema = z.union([
    z.strictObject({ id: requirementId, passed: z.literal(true) }),
    z.strictObject({ id: requirementId, passed: z.literal(false), reason: requirementFailure }),
]);
/** Admin confirm (#4045 step 3): one queued task per eligible product, each with Web's real task state. */
export const opportunitySetupConfirmSchema = z.strictObject({
    shop: text,
    connectionId: text,
    previewHash: sha256,
    model: text,
    tasks: z.array(z.strictObject({
        productId: shopifyProductId,
        taskId: text,
        status: z.enum(['queued', 'claimed', 'retryable', 'succeeded', 'failed_terminal', 'cancelled']),
    })),
});
/** Admin read of the saved setups for one store (#4045 step 3), exactly as stored, newest first. */
export const opportunitySetupListSchema = z.strictObject({
    shop: text,
    connectionId: text,
    setups: z.array(z.strictObject({
        batchId: text,
        taskId: text.nullable(),
        generatedAt: text,
        /** True when Insights offers this retained comparison beside its current product finding. */
        offeredOnCard: z.boolean(),
        setup: retainedOpportunityStudySetupSchema,
    })),
});
//# sourceMappingURL=opportunity-study-setup.js.map